/**
 * @file ページからヘッダーへ「いま選んでいるまなぶ人」を渡す仕組み。
 *
 * @what: ページが `useHeaderLearner({ options, selectedId, onSelect })` を呼ぶと、
 *   ヘッダー左の LearnerSwitcher が選んでいるまなぶ人を出し、押すと候補から選び直せる。
 *   ページを離れると消える。
 * @why: まなぶ人の切り替えが本文のタブに埋もれ、いまだれの記録を見ているのかが常には
 *   見えなかった (代表 2026-08-29)。ヘッダーの Berry Buds の表記をやめ、空いた場所に
 *   まなぶ人を出す。
 * @failure: Layout の外 (テスト等) では setter が何もしない既定値になる。
 */

import { createContext, useContext, useEffect } from "react";

export interface HeaderLearnerOption {
  id: string;
  /** 一覧に出す表示名で、省略しない。 */
  label: string;
}

export interface HeaderLearner {
  options: HeaderLearnerOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export interface HeaderLearnerContextValue {
  learner: HeaderLearner | null;
  setLearner: (learner: HeaderLearner | null) => void;
}

export const HeaderLearnerContext = createContext<HeaderLearnerContextValue>({
  learner: null,
  setLearner: () => {},
});

/** いま選んでいるまなぶ人をヘッダーに渡す (null で消す)。unmount で必ず消す。 */
export function useHeaderLearner(learner: HeaderLearner | null): void {
  const { setLearner } = useContext(HeaderLearnerContext);
  const options = learner?.options ?? null;
  const selectedId = learner?.selectedId ?? null;
  const onSelect = learner?.onSelect ?? null;
  useEffect(() => {
    setLearner(options === null || onSelect === null ? null : { options, selectedId, onSelect });
    return () => setLearner(null);
  }, [setLearner, options, selectedId, onSelect]);
}

/**
 * ナビゲーションバーに出す名前を短くする。
 *
 * @what: 5文字まではそのまま返し、6文字目からは切って "..." を付ける
 * @why: ヘッダーは横幅を取れないので、長い名前でも並びが崩れないようにする。切った跡に
 *   空白が残ると "お子さん ..." のように間延びするため、末尾の空白は落とす。
 * @failure: 数えるのはコードポイントで、書記素ではない。絵文字と異体字は正しく数えるが、
 *   結合文字を使う名前では見た目の文字数と1文字ずれることがある。
 */
export function truncateLearnerLabel(label: string, max = 5): string {
  const chars = [...label];
  if (chars.length <= max) return label;
  return `${chars.slice(0, max).join("").trimEnd()}...`;
}
